import { Typography, TypographyVariant } from '@mui/material';
import { createRef, useMemo } from 'react';
import { CSSTransition, TransitionGroup } from 'react-transition-group';

import styles from './SlidingTransitionGroup.module.scss';


interface SlidingTransitionGroupProps {
    textContent: string;
    direction: 'up' | 'down'; // TODO
    variant?: TypographyVariant;
}

/**
 * Renders a transition group that gives a slot-machine-like effect of the previous text content sliding away
 * and the new text content sliding in
 *
 * @param textContent - The text content of the node, changes to this will trigger the transition effect
 * @param variant     - The variant of the sliding text
 * @param direction   - The direction that the new text content should enter from
 * @constructor
 */
export function SlidingTransitionGroup({ textContent, variant, direction }: SlidingTransitionGroupProps) {
    // The nodeRef needs to change with the key of the CSSTransition that is rendered below, so we useMemo to
    // regenerate the ref when the text content changes instead of using useRef
    // eslint-disable-next-line react-hooks/exhaustive-deps
    const nodeRef = useMemo(() => createRef<HTMLDivElement>(), [ textContent ]);

    return (
        <TransitionGroup className={styles.transitionContainer}>
            <CSSTransition
                nodeRef={nodeRef}
                key={textContent}
                timeout={250}
                classNames={getSlideCSSClassNames(direction)}
            >
                <Typography
                    ref={nodeRef}
                    color="textSecondary"
                    component="div"
                    variant={variant || 'body2'}
                >
                    {textContent}
                </Typography>
            </CSSTransition>
        </TransitionGroup>
    );
}

function getSlideCSSClassNames(direction: 'up' | 'down') {
    const keyBase = `slide_${direction}`;

    return {
        appear: styles[`${keyBase}_appear`],
        appearActive: styles[`${keyBase}_appear_active`],
        enter: styles[`${keyBase}_enter`],
        enterActive: styles[`${keyBase}_enter_active`],
        exit: styles[`${keyBase}_exit`],
        exitActive: styles[`${keyBase}_exit_active`]
    };
}
